// frontend/src/pages/AIMentor.tsx

import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAiMentorChat } from '../hooks/useAiMentorChat';
import { ChatBubble } from '../components/ai/ChatBubble';
import { TypingIndicator } from '../components/ai/TypingIndicator';
import { QuickTopics } from '../components/ai/QuickTopics';
import { GeminiModel, StudentContext } from '../types/ai.types';
import { FeatureGate } from '../components/common/FeatureGate';
import { usePlanUsage } from '../hooks/usePlanUsage';
import { UsageBanner } from '../components/common/UsageBanner';
import { PlanLimitBar } from '../components/common/PlanLimitBar';
import { useAuthStore } from '../store/authStore';
import { stopAllAudioAndTTS } from '../utils/speechQueueManager';
import '../styles/ai-mentor.css';

const MOCK_STUDENT_CONTEXT: StudentContext = {
  age: 10,
  level: 'beginner',
  currentTopic: 'Döngüler',
};

export function AIMentorPage() {
  const { messages, isLoading, sendMessage, clearChat, model, setModel, isQuotaExceeded, isGlobalAudioPlaying, toggleGlobalAudio } =
    useAiMentorChat(MOCK_STUDENT_CONTEXT);
  const [inputValue, setInputValue] = useState('');
  const { isAtLimit, isNearLimit, usage, refetch: refetchUsage } = usePlanUsage();
  const profile = useAuthStore(s => s.profile);

  // Sayfadan çıkıldığında veya başka rotaya gidildiğinde tüm sesleri durdur
  useEffect(() => {
    return () => {
      stopAllAudioAndTTS();
    };
  }, []);

  // Günlük AI mesaj limiti dolmuş mu?
  const aiAtLimit = isAtLimit('ai_message');
  const aiNearLimit = isNearLimit('ai_message');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!inputValue.trim() || isQuotaExceeded || aiAtLimit) return;
    sendMessage(inputValue);
    setInputValue('');
    setTimeout(refetchUsage, 2000);
  }

  function handleQuickTopic(prompt: string) {
    if (aiAtLimit) return;
    sendMessage(prompt);
    setTimeout(refetchUsage, 2000);
  }

  return (
    <div className="ai-mentor-page">
      <header className="ai-mentor-header">
        <h1>AI Mentor 🤖</h1>
        <p>Kodlama, Matematik, Fen, Türkçe ve tüm derslerde kişiselleştirilmiş AI öğretmenin her zaman yanında</p>
      </header>

      <div className="ai-mentor-grid">
        <section>
          {/* Kullanım banner */}
          <UsageBanner type="ai_message" />

          {/* ── Limit dolmuşsa büyük kilit ekranı ── */}
          {aiAtLimit && usage ? (
            <div style={{
              background: 'var(--bg-card)',
              border: '1.5px solid #ef444440',
              borderRadius: 16,
              padding: '40px 32px',
              textAlign: 'center',
              marginBottom: 16,
            }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>🚫</div>
              <h2 style={{ color: '#ef4444', marginBottom: 8 }}>Günlük AI Mesaj Limitine Ulaştın</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 6, fontSize: 14 }}>
                <strong>{usage.planName}</strong> planında günlük{' '}
                <strong>{usage.aiMessagesLimit}</strong> mesaj hakkın var.{' '}
                <strong>{usage.aiMessagesUsed}</strong> mesaj kullandın.
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
                🔄 Limit yarın gece yarısı (UTC) sıfırlanır.
              </p>
              <div style={{ maxWidth: 340, margin: '0 auto 24px' }}>
                <PlanLimitBar show={['ai_message']} />
              </div>
              <Link
                to="/pricing"
                className="lesson-btn primary"
                style={{ display: 'inline-block' }}
              >
                💎 Sınırsız Pakete Geç
              </Link>
            </div>
          ) : (
            <div className="ai-mentor-chat-container">
              <div className="ai-mentor-chat-header">
                <div className="ai-mentor-avatar large">🤖</div>
                <div>
                  <div className="ai-mentor-bot-name">MinikBot</div>
                  <div className="ai-mentor-bot-status">
                    AI Mentor • Çevrimiçi
                    {usage && usage.aiMessagesLimit !== null && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: aiNearLimit ? '#f59e0b' : 'var(--text-muted)' }}>
                        ({usage.aiMessagesUsed}/{usage.aiMessagesLimit} mesaj)
                      </span>
                    )}
                  </div>
                </div>
                <button type="button" className="ai-mentor-clear-btn" onClick={clearChat}>
                  🗑️ Temizle
                </button>
              </div>

              <div className="ai-mentor-messages" role="log" aria-live="polite">
                {messages.map((msg, index) => (
                  <ChatBubble 
                    key={msg.id} 
                    message={msg} 
                    isLastMessage={index === messages.length - 1}
                    globalAudioState={{ isPlaying: isGlobalAudioPlaying, toggle: toggleGlobalAudio }}
                  />
                ))}
                {isLoading && <TypingIndicator />}
              </div>

               <form className="ai-mentor-input-area" onSubmit={handleSubmit}>
                <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    className="ai-mentor-input"
                    placeholder="Bir ders sorusu sor veya konu anlatımı iste..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    maxLength={3900}
                    disabled={isLoading}
                    aria-label="Mesajınızı yazın"
                    style={{ paddingRight: '80px', width: '100%' }}
                  />
                  {inputValue.length > 0 && (
                    <span style={{
                      position: 'absolute',
                      right: '16px',
                      fontSize: '11px',
                      color: inputValue.length > 3600 ? '#ef4444' : 'var(--text-muted, #94a3b8)',
                      pointerEvents: 'none',
                      fontWeight: 500
                    }}>
                      {inputValue.length}/3900
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  className="ai-mentor-send-btn"
                  disabled={isLoading || !inputValue.trim()}
                  aria-label="Gönder"
                >
                  📤
                </button>
              </form>
            </div>
          )}

          <div className="ai-mentor-quick-row">
            {[
              { icon: '📐', label: 'Kesirler nedir?',     prompt: '📐 Matematik: Kesirler konusunu anlatır mısın?' },
              { icon: '🐍', label: 'Python Döngüler?',    prompt: '🐍 Python: For döngüsü mantığı nedir?' },
              { icon: '🧪', label: 'Fotosentez nedir?',   prompt: '🧪 Fen: Fotosentez nasıl gerçekleşir?' },
              { icon: '🇬🇧', label: 'İngilizce Pratik',   prompt: '🇬🇧 İngilizce: Günlük hayatta en çok kullanılan 10 cümle nedir?' },
              { icon: '💡', label: 'Örnek ver',           prompt: '💡 Bana somut bir günlük hayat örneği ver' },
              { icon: '🐣', label: 'Daha basit anlat',    prompt: '🐣 Konuyu daha basit bir dille anlat' },
            ].map(({ icon, label, prompt }) => (
              <button
                key={label}
                type="button"
                onClick={() => handleQuickTopic(prompt)}
                disabled={aiAtLimit}
                style={aiAtLimit ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        </section>

        <aside>
          <div className="ai-mentor-card">
            <h3>Hızlı Konular</h3>
            <QuickTopics onSelectTopic={aiAtLimit ? () => {} : handleQuickTopic} />
          </div>

          {/* Plan kullanım özeti sidebar */}
          {usage && (
            <div className="ai-mentor-card" style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 13, marginBottom: 12, color: 'var(--text-secondary)' }}>📊 Plan Kullanımı</h3>
              <PlanLimitBar compact />
            </div>
          )}

        </aside>
      </div>
    </div>
  );
}
